import { useEffect, useState } from 'react';
import type { AccountStatus } from '@/app/auth/services/code-flow.service';
import { fetchAccountStatus, requestResetCode, startInvite } from '@/app/auth/services/code-flow.service';
import { getRememberedLogin } from '@/app/auth/lib/remembered-login';
import { CodeStep } from './components/CodeStep';
import { LoginForm } from './components/LoginForm';
import { SetPasswordStep } from './components/SetPasswordStep';
import { UnknownEmailStep } from './components/UnknownEmailStep';

type Step = 'email' | 'password' | 'code' | 'setPassword' | 'unknown';
type Flow = 'invite' | 'reset';

export const AuthLoginFlow = () => {
  const [step, setStep] = useState<Step>('email');
  const [flow, setFlow] = useState<Flow>('invite');
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get('email') ?? '',
  );
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeOrigin, setCodeOrigin] = useState<Step>('email');

  useEffect(() => {
    void (async () => {
      const loaded = await getRememberedLogin();
      const fromUrl = new URLSearchParams(window.location.search).get('email');
      if (loaded && !fromUrl) {
        setEmail(loaded.email);
        setPassword(loaded.password);
        setStep('password');
      }
    })();
  }, []);

  const handleStatus = (nextEmail: string, status: AccountStatus) => {
    setEmail(nextEmail);
    setCode('');
    if (status === 'login') {
      setStep('password');
      return;
    }
    if (status === 'invite') {
      setFlow('invite');
      setCodeOrigin('email');
      setStep('code');
      void startInvite(nextEmail);
      return;
    }
    setStep('unknown');
  };

  const handleForgotPassword = async () => {
    // The server reset endpoints no-op (anti-enumeration) for passwordless or
    // deleted accounts, so check status first and reuse the invite flow.
    const status = await fetchAccountStatus(email);
    if (status === 'invite') {
      setFlow('invite');
      setCodeOrigin('password');
      setStep('code');
      void startInvite(email);
      return;
    }
    if (status === 'login') {
      setFlow('reset');
      setCodeOrigin('password');
      setStep('code');
      void requestResetCode(email);
      return;
    }
    setStep('unknown');
  };

  if (step === 'email' || step === 'password') {
    return (
      <LoginForm
        email={email}
        mode={step === 'password' ? 'password' : 'email'}
        onEmailChange={setEmail}
        onForgotPassword={handleForgotPassword}
        onPasswordChange={setPassword}
        onStatus={handleStatus}
        password={password}
      />
    );
  }

  if (step === 'code') {
    return (
      <CodeStep
        email={email}
        onBack={() => {
          setStep(codeOrigin);
        }}
        onCode={(nextCode) => {
          setCode(nextCode);
          setStep('setPassword');
        }}
        purpose={flow}
      />
    );
  }

  if (step === 'setPassword') {
    return (
      <SetPasswordStep
        code={code}
        email={email}
        mode={flow}
        onBack={() => {
          setStep('code');
        }}
      />
    );
  }

  return (
    <UnknownEmailStep
      email={email}
      onBack={() => {
        setStep('email');
      }}
    />
  );
};
